import Head from 'next/head';
import React, { useCallback, useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { getRentals, goToRentals } from '@/actions/rentals';
import PopularPostsList from '@/components/blog/PopularPostsList';
import MaxWidth from '@/components/common/layout/MaxWidth';
import NarrowWidth from '@/components/common/layout/NarrowWidth';
import { RentalsInlineList } from '@/components/common/RentalsList/RentalsInlineList';
import { BlogMenu } from '@/components/route/blog/BlogMenu';
import BlogPostHero from '@/components/route/blog/BlogPostHero/BlogPostHero';
import CategoryPosts from '@/components/route/blog/CategoryPosts';
import CategoryTitleLink from '@/components/route/blog/CategoryTitleLink';
import FeaturedPosts from '@/components/route/blog/FeaturedPosts';
import RentalRigs from '@/components/route/blog/RentalRigs';
import { RentYourRV } from '@/components/route/blog/RentYourRV';
import Heading from '@/components/switchback/Heading/Heading';
import NewsLetterSignup from '@/components/switchback/NewsLetterSignup/NewsLetterSignup';
import { useCaptainBlogAI } from '@/hooks/useCaptainBlogAI';
import { EListingSource } from '@/services/analytics/listings/types';
import { getCategoryPosts, SuccessPostResponse } from '@/services/getBlogCategoryPosts';
import getBlogRecentPosts, { FeaturedPostsIDs } from '@/services/getBlogRecentPosts';
import { trackEvent } from '@/services/track-event';
import { IBlogPost } from '@/services/types/blog/post.types';
import hash from '@/utility/hash';
import withRedirect from '@/utility/withRedirect';

export const getServerSideProps = withRedirect(
  { destination: '/blog', permanent: false },
  async () => {
    const results = await hash({
      recentPosts: getBlogRecentPosts({
        limit: 4,
        featuredId: FeaturedPostsIDs.featuredOwner,
      }),
      rvMaintenance: getCategoryPosts('rv-maintenance', 3),
      rvRemodelingDIY: getCategoryPosts('rv-remodeling-diy', 3),
      rvBusinessTips: getCategoryPosts('rv-business-tips', 3),
      outdoorsyOwnerStories: getCategoryPosts('owner-stories', 3),
      topPosts: getCategoryPosts('top-blogs', 20),
    });
    return { props: results };
  },
);

interface IForOwnersProps {
  recentPosts: IBlogPost[];
  rvMaintenance: SuccessPostResponse;
  rvRemodelingDIY: SuccessPostResponse;
  rvBusinessTips: SuccessPostResponse;
  outdoorsyOwnerStories: SuccessPostResponse;
  topPosts: SuccessPostResponse;
}

const ForOwners: React.FC<IForOwnersProps> = ({
  recentPosts,
  rvMaintenance,
  rvRemodelingDIY,
  rvBusinessTips,
  outdoorsyOwnerStories,
  topPosts,
}) => {
  const intl = useIntl();
  const newsletterTitle = intl.formatMessage({
    defaultMessage: 'Be the first to get doses of destination inspiration, and discount codes.',
    id: 'RdFytN',
  });
  const pageType = 'Blog page';
  const dispatch = useDispatch();
  const getPageRentals = useCallback((filters: any) => dispatch(getRentals(filters)), [dispatch]);

  useCaptainBlogAI();

  useEffect(() => {
    getPageRentals({ count: 5 });

    trackEvent({
      event: 'Blog : Viewed page',
      pageType,
    });
  }, [getPageRentals]);

  return (
    <>
      <Head>
        <title>RV Stories, Motorhomes, Camper Rentals & Airstreams | Outdoorsy.com</title>
        <link rel="canonical" href="https://www.outdoorsy.com/blog/for-owners" />

        <meta
          name="description"
          content="Outdoorsy's unique blog for RV travel and RV rental. Useful guides for Class A, B and C motorhomes, incredible campervan stories, and lots of cool RVs."
        />

        <meta property="fb:app_id" content="604465049688341" />
        <meta name="msvalidate.01" content="1DD3784B3E7F8CD7B4C595940717F938" />
        <meta name="apple-itunes-app" content="app-id=1443835927, affiliate-data=1010lvba" />
        <meta
          name="google-site-verification"
          content="DuUf3p3Pr3-gmZfpMIg3gyG75gl7C3JpXWb0-ilQJbI"
        />

        <link rel="dns-prefetch" href="//s.w.org" />

        {intl.locale === 'en-us' ? (
          <meta name="robots" content="index, follow, max-image-preview:large" />
        ) : (
          <meta name="robots" content="noindex" />
        )}

        <meta
          name="googlebot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />
        <meta
          name="bingbot"
          content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"
        />

        <meta property="og:locale" content="en_US" />
        <meta property="og:type" content="article" />
        <meta
          property="og:title"
          content="RV Stories, Motorhomes, Camper Rentals & Airstreams | Outdoorsy.com"
        />
        <meta property="og:url" content="https://www.outdoorsy.com/blog/for-owners" />
        <meta property="og:site_name" content="Outdoorsy RV Blogs" />
        <meta
          property="og:description"
          content="Outdoorsy's unique blog for RV travel and RV rental. Useful guides for Class A, B and C motorhomes, incredible campervan stories, and lots of cool RVs."
        />
        <meta name="twitter:card" content="summary" />
      </Head>

      {recentPosts[0] && <BlogPostHero post={recentPosts[0]} />}

      <div className="relative z-50 mx-auto">
        <BlogMenu />
      </div>

      <hr />

      <MaxWidth className="m-auto mt-12">
        <Heading level={1} className="mb-8 highlight autoType800">
          {intl.formatMessage({
            defaultMessage: 'New and noteworthy host articles.',
            id: 'XZvYV0',
          })}
        </Heading>
        <FeaturedPosts posts={recentPosts.slice(1, 4)} />
        <hr />
      </MaxWidth>

      <NarrowWidth className="mt-12 lg:max-w-lg" collapseMobile>
        <CategoryTitleLink slug={rvMaintenance.slug} title="RV maintenance." />
        <CategoryPosts posts={rvMaintenance.posts} inline />
      </NarrowWidth>

      <NarrowWidth className="mt-12 lg:max-w-lg" collapseMobile>
        <CategoryTitleLink slug={rvRemodelingDIY.slug} title="RV remodeling DIY." />
        <CategoryPosts posts={rvRemodelingDIY.posts} inline />
      </NarrowWidth>

      <NarrowWidth className="mt-24 lg:max-w-lg" collapseMobile>
        <RentYourRV />
      </NarrowWidth>

      <NarrowWidth className="mt-12 lg:max-w-lg" collapseMobile>
        <CategoryTitleLink
          slug={rvBusinessTips.slug}
          title="Tips for running an RV rental buisness."
        />
        <CategoryPosts posts={rvBusinessTips.posts} inline />
      </NarrowWidth>

      <MaxWidth className="m-auto mt-12">
        <hr />
        <Heading className="my-8 highlight autoType800">
          {intl.formatMessage({ defaultMessage: 'Outdoorsy host stories.', id: 'C2H8oJ' })}
        </Heading>
        <FeaturedPosts posts={outdoorsyOwnerStories.posts} />
      </MaxWidth>

      <MaxWidth className="m-auto mt-12">
        <hr />
        <Heading level={2} className="mx-4 my-8 highlight autoType800 lg:mx-0">
          {intl.formatMessage({ defaultMessage: 'Ready to get started.', id: 'EMpVYE' })}
        </Heading>
        <RentalsInlineList
          goToRentals={() => dispatch(goToRentals())}
          eventSource={EListingSource.GRID_LAYOUT}
        />
      </MaxWidth>

      <MaxWidth collapseMobile className="m-auto my-12">
        <Heading level={2} className="mx-4 my-8 highlight autoType800 lg:mx-0">
          {intl.formatMessage({ defaultMessage: 'Travel the way you want it.', id: '7ndkZ9' })}
        </Heading>
        <RentalRigs />
      </MaxWidth>

      <PopularPostsList topPosts={topPosts} />

      <div className="py-2 mt-4 bg-white">
        <MaxWidth collapseMobile className="m-auto my-12">
          <NewsLetterSignup title={newsletterTitle} source={pageType} />
        </MaxWidth>
      </div>
    </>
  );
};

export default ForOwners;
